import { useEffect, useState } from 'react';

import { prefersReducedMotion, supportsWebGL } from './webgl';

/**
 * How much scene this device should be asked to draw.
 *
 * A marketing hero that runs at 12fps on a mid-range Android is worse than no
 * hero at all: it stutters the scroll, drains the battery and makes the product
 * look slow. So the scene is sized to the machine rather than drawn at full
 * detail everywhere and hoped for.
 *
 * - `none`     no WebGL, or reduced-motion requested → static hero
 * - `low`      phones and weak GPUs → few particles, DPR 1, no post-processing
 * - `medium`   most laptops
 * - `high`     desktops with real GPUs and plenty of cores
 */
export type DeviceTier = 'none' | 'low' | 'medium' | 'high';

export interface TierSettings {
  tier: DeviceTier;
  /** Instanced node count for the message field. */
  particles: number;
  /** Device pixel ratio ceiling. Above ~2 the cost is invisible but real. */
  dpr: [number, number];
  /** Whether the drifting light rig animates every frame. */
  animateLights: boolean;
}

const SETTINGS: Record<DeviceTier, Omit<TierSettings, 'tier'>> = {
  none: { particles: 0, dpr: [1, 1], animateLights: false },
  low: { particles: 90, dpr: [1, 1.25], animateLights: false },
  medium: { particles: 260, dpr: [1, 1.75], animateLights: true },
  high: { particles: 520, dpr: [1, 2], animateLights: true },
};

function detect(): DeviceTier {
  if (!supportsWebGL() || prefersReducedMotion()) return 'none';

  const nav = navigator as Navigator & { deviceMemory?: number; hardwareConcurrency?: number };
  const cores = nav.hardwareConcurrency ?? 4;
  // Reported by Chrome only, in GB. Absent elsewhere, so it can only ever
  // downgrade a decision, never be required for one.
  const memory = nav.deviceMemory ?? 0;
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  const narrow = window.innerWidth < 820;

  // Touch phones get the low tier regardless of core count: the SoC throttles
  // under sustained load in a way a desktop does not, and the thermal cost shows
  // up as scroll jank a few seconds in.
  if (coarse || narrow) return 'low';
  if (cores <= 4 || (memory > 0 && memory <= 4)) return 'low';
  if (cores >= 8 && (memory === 0 || memory >= 8)) return 'high';

  return 'medium';
}

/**
 * Resolved on the client only. The first render always returns `none` so the
 * server-rendered and hydrated markup agree, and the scene mounts on the next
 * tick — which also keeps the Canvas out of the critical paint.
 */
export function useDeviceTier(): TierSettings {
  const [tier, setTier] = useState<DeviceTier>('none');

  useEffect(() => {
    setTier(detect());

    // Someone can turn reduced-motion on while the page is open, and a hero that
    // keeps spinning after they did is exactly the thing they asked to stop.
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (): void => {
      setTier(media.matches ? 'none' : detect());
    };

    media.addEventListener('change', onChange);
    return () => {
      media.removeEventListener('change', onChange);
    };
  }, []);

  return { tier, ...SETTINGS[tier] };
}
